import { Stack } from '@mui/material'
import { useSelector } from 'react-redux'

import { useBuildProgressModal } from '@/components/modals/BuildProgressModal'
import { useCopyOrdersModal } from '@/components/modals/CopyOrdersModal'
import { useRouteOptimizationModal } from '@/components/modals/RouteOptimizationModal'
import { Tooltip } from '@/components/primitives/Tooltip'
import { ReadOnlyTooltip } from '@/components/smartUtils/plan/ReadOnlyTooltip'
import { StorageIndicator } from '@/components/StorageIndicator'
import { selectUserConfiguration } from '@/features/domain/user'
import { PaperPlane } from '@/icons'

import { useActions } from '../hooks/useActions'
import { useItemsPermissions } from '../hooks/useItemsPermissions'
import { StyledCopyIcon } from '../hooks/useStyles'
import { useTexts } from '../useTexts'

import { AddOrderButton } from './AddOrderButton'
import { BuildRoutesButton } from './BuildRoutesButton'
import { Separator } from './Separator'
import { SidebarRowButton } from './SidebarRowButton'

export function Operations() {
  const { show: showBuildProgressModal, Modal: BuildProgressModal } = useBuildProgressModal()
  const { show: showCopyOrdersModal, Modal: CopyOrdersModal } = useCopyOrdersModal()
  const { show: showRouteOptimizationModal, Modal: RouteOptimizationModal } =
    useRouteOptimizationModal()
  const {
    addOrderPermissions,
    buildRoutesPermissions,
    dispatchRoutesPermissions,
    copyToOperationsPermissions,
  } = useItemsPermissions()

  const { addOrder, buildRoutes, customBuild, showDispatchPanel } = useActions(
    showBuildProgressModal,
    showCopyOrdersModal,
    showRouteOptimizationModal,
  )

  const { planType } = useSelector(selectUserConfiguration)
  const texts = useTexts()

  const isArchivedDay = planType === 'archived'

  return (
    <Stack marginTop={2} padding="0 24px 24px 16px" spacing={1} width="100%">
      <ReadOnlyTooltip
        placement="right"
        canEdit={!addOrderPermissions.disabled}
        title={addOrderPermissions.tooltip.title}
        subtitle={addOrderPermissions.tooltip.subtitle}
        render={preventEditing => (
          <AddOrderButton
            disabled={preventEditing}
            onAddOrder={addOrder}
            testid="sidebar-operations__add-order-button"
          />
        )}
      />

      <Separator />

      <ReadOnlyTooltip
        placement="right"
        canEdit={!buildRoutesPermissions.disabled}
        title={buildRoutesPermissions.tooltip.title}
        subtitle={buildRoutesPermissions.tooltip.subtitle}
        forceState={buildRoutesPermissions.tooltip.forceState}
        render={preventEditing => (
          <BuildRoutesButton
            disabled={preventEditing}
            onInstantBuild={buildRoutes}
            onCustomBuild={customBuild}
            testid="sidebar-operations__build-button"
          />
        )}
      />

      <Separator />

      {isArchivedDay ? (
        /**
         * That tooltip can't actually be managed via `ReadOnlyTooltip` since it doesn't represent
         * a change to the archived day but to Operations
         **/
        <Tooltip
          placement="right"
          disabled={!copyToOperationsPermissions.disabled}
          title={copyToOperationsPermissions.tooltip.title}
          subtitle={copyToOperationsPermissions.tooltip.subtitle}
        >
          <SidebarRowButton
            icon={
              <StyledCopyIcon
                color={copyToOperationsPermissions.disabled ? 'disabled' : undefined}
              />
            }
            disabled={copyToOperationsPermissions.disabled}
            onClick={showCopyOrdersModal}
            label={texts.copy}
            testid="sidebar-operations__copy-button"
          />
        </Tooltip>
      ) : (
        <ReadOnlyTooltip
          render={preventEditing => (
            <SidebarRowButton
              icon={<PaperPlane size={14} />}
              disabled={preventEditing || dispatchRoutesPermissions.disabled}
              onClick={showDispatchPanel}
              label={texts.dispatchRoutes}
              testid="sidebar-operations__dispatch-routes-button"
            />
          )}
        />
      )}

      <StorageIndicator />

      <BuildProgressModal />
      <CopyOrdersModal />
      <RouteOptimizationModal />
    </Stack>
  )
}
